"use client";

import { useTranslations } from "next-intl";
import type { ReviewForClient } from "@/features/reviews/shared/types";
import { Image } from "@/lib/client/components/Image";
import { LikesEnum } from "@/lib/shared/types/LikesEnum";
import { Box, HStack, StarRating, Text, VStack } from "@/ui";

interface ReviewCardProps {
  review: ReviewForClient;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function ReviewCard({ review }: ReviewCardProps) {
  const reviewsT = useTranslations("reviews");

  const likeLabels: Record<LikesEnum, string> = {
    [LikesEnum.DESIGN]: reviewsT("design"),
    [LikesEnum.MATERIAL]: reviewsT("material"),
    [LikesEnum.PRICE]: reviewsT("price"),
  };

  return (
    <VStack className="border-b border-border py-6 gap-4">
      <HStack className="justify-between items-start">
        <VStack className="gap-1">
          <HStack className="gap-2 items-center">
            <Text className="font-semibold">{review.author || reviewsT("guest")}</Text>
            {review.verified && (
              <Box className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                {reviewsT("verified")}
              </Box>
            )}
          </HStack>
          <StarRating rating={review.rating} />
        </VStack>
        <Text className="text-sm text-muted-foreground">{formatDate(review.createdAt)}</Text>
      </HStack>

      {/* Review content */}
      <Text className="text-foreground">{review.content}</Text>

      {/* Likes/tags */}
      {review.likes && review.likes.length > 0 && (
        <HStack className="gap-2 flex-wrap">
          {review.likes.map((like) => (
            <Box
              key={like}
              className="bg-muted text-muted-foreground text-sm px-3 py-1 rounded-full"
            >
              {likeLabels[like as LikesEnum]}
            </Box>
          ))}
        </HStack>
      )}

      {/* Review images */}
      {review.images && review.images.length > 0 && (
        <HStack className="gap-2 flex-wrap mt-2">
          {review.images.map((img, index) =>
            img.image?.url ? (
              <Box
                key={index}
                className="relative w-20 h-20 rounded-md overflow-hidden"
              >
                <Image
                  src={img.image.url}
                  alt={`${reviewsT("images")} ${index + 1}`}
                  fill
                  className="object-cover"
                  sizes="80px"
                />
              </Box>
            ) : null,
          )}
        </HStack>
      )}
    </VStack>
  );
}
